import React, { useState } from 'react';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
type Status = 'idle' | 'submitting' | 'success';
interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}
const inputClass = 'mt-1.5 w-full rounded-md border bg-white px-4 py-3 text-base text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-navy/20';
export function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<FormErrors>({});
  const [values, setValues] = useState({
    name: '',
    email: '',
    topic: 'General question',
    message: ''
  });
  const update = (field: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setValues(v => ({
    ...v,
    [field]: e.target.value
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: FormErrors = {};
    if (!values.name.trim()) next.name = 'Please enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(values.email)) next.email = 'Please enter a valid email address.';
    if (values.message.trim().length < 10) next.message = 'Please add a few more details.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  if (status === 'success') {
    return <div className="rounded-xl border border-line bg-cccia-light p-8 text-center" role="status" data-id="element-40" data-mp="36mkjgdf4npwi">
        <CheckCircle2Icon className="mx-auto h-10 w-10 text-cccia" aria-hidden="true" data-id="element-41" data-mp="3bl5hc66wpxhs" />
        <p className="mt-3 text-xl font-semibold text-ink" data-id="element-42" data-mp="3gki7azt2rkol">Thanks, {values.name.split(' ')[0]}!</p>
        <p className="mt-1 text-muted" data-id="element-43" data-mp="3km3r75410z8a">Your message was sent. The CCCIA team will get back to you soon.</p>
        <button type="button" onClick={() => {
        setValues({
          name: '',
          email: '',
          topic: 'General question',
          message: ''
        });
        setStatus('idle');
      }} className="mt-5 text-sm font-semibold text-orange-deep hover:text-orange-deeper" data-id="element-44" data-mp="vujbfopb122c">
          Send another message
        </button>
      </div>;
  }
  const border = (err?: string) => err ? 'border-red-600' : 'border-line focus:border-navy';
  return <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-45" data-mp="35ut50t90bx2c">
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-46" data-mp="30ylwqtt529h8">
        <div data-id="element-47" data-mp="2d88dnssj7kys">
          <label htmlFor="name" className="text-sm font-medium text-ink" data-id="element-48" data-mp="2d7zxns143zjd">Name</label>
          <input id="name" value={values.name} onChange={update('name')} className={`${inputClass} ${border(errors.name)}`} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} data-id="element-49" data-mp="2976efbym78wt" />
          {errors.name && <p id="name-error" className="mt-1 text-sm text-red-700" data-id="element-50" data-mp="2i6jmq1b05rfy">{errors.name}</p>}
        </div>
        <div data-id="element-51" data-mp="1ugys2l5hl5qu">
          <label htmlFor="email" className="text-sm font-medium text-ink" data-id="element-52" data-mp="1ugp9vjqoo2vp">Email</label>
          <input id="email" type="email" value={values.email} onChange={update('email')} className={`${inputClass} ${border(errors.email)}`} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-53" data-mp="1phw0a2eiow0t" />
          {errors.email && <p id="email-error" className="mt-1 text-sm text-red-700" data-id="element-54" data-mp="9tfsmpyh4en4">{errors.email}</p>}
        </div>
      </div>
      <div data-id="element-55" data-mp="z1it2wppwbe">
        <label htmlFor="topic" className="text-sm font-medium text-ink" data-id="element-56" data-mp="yrstfbo3p5p">What’s this about?</label>
        <select id="topic" value={values.topic} onChange={update('topic')} className={`${inputClass} border-line focus:border-navy`} data-id="element-57" data-mp="oocck0fnaqg1">
          <option data-id="element-58" data-mp="sp5wkipyvl3n">General question</option>
          <option data-id="element-59" data-mp="eqoh18a7tewt">Visiting the Showcase</option>
          <option data-id="element-60" data-mp="epwibolbn12b">Entering a model home (builders)</option>
          <option data-id="element-61" data-mp="jpivjy15ghb4">Sponsorship & advertising</option>
        </select>
      </div>
      <div data-id="element-62" data-mp="jrlvpaw0nqtk">
        <label htmlFor="message" className="text-sm font-medium text-ink" data-id="element-63" data-mp="2zgx0y1fgzum4">Message</label>
        <textarea id="message" rows={5} value={values.message} onChange={update('message')} className={`${inputClass} ${border(errors.message)}`} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-error' : undefined} data-id="element-64" data-mp="2zj9r31vdtba9" />
        {errors.message && <p id="message-error" className="mt-1 text-sm text-red-700" data-id="element-65" data-mp="3exdhe6z809ko">{errors.message}</p>}
      </div>
      <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-orange-deep px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 hover:bg-orange-deeper disabled:opacity-70 sm:w-auto" data-id="element-66" data-mp="3axuwhb7efyhu">
        {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-67" data-mp="3tnukrvvf19k7" />}
        {status === 'submitting' ? 'Sending…' : 'Send message'}
      </button>
    </form>;
}